export enum IssuePriority {
  High = 10,
  Medium = 5,
  Low = 1,
  NA = 0,
}

/**
 * Represents a roadmap summary for a specific chapter or part of a book.
 */
export interface RoadmapSummaryByChapter {
  /**
   * An array of issues identified within the chapter/part.
   */
  issues: Issue[];

  /**
   * A friendly identifier for the chapter/part (e.g., "part-i").
   */
  friendly_id: string;

  /**
   * The chapter number (as a string).
   */
  chapter_number: string;

  /**
   * The title of the chapter.
   */
  chapter_title?: string;

  /**
   * A brief description of the chapter's purpose or function within the overall narrative.
   */
  chapter_function: string;

  /**
   * An overview of the analysis of the chapter, highlighting key findings and areas for improvement.
   */
  analysis_overview: string;

  /**
   * A summary of the chapter's strengths, outlining what works well.
   */
  chapter_strengths: string;
}

/**
 * Represents an implementation idea object format that might be returned by the API
 */
export interface ImplementationIdea {
  idea: string;
  [key: string]: any; // Allow for additional properties
}

/**
 * Represents a specific issue identified within a chapter/part, OR overall.
 * This is reused for both chapter-specific and overall feedback.
 */
export interface Issue {
  /**
   * The area or category the issue falls under (e.g., "Plot-Coherence", "Character-Analysis").
   */
  area: string;

  /**
   * The priority level of the issue ("High", "Medium").
   */
  priority: IssuePriority;

  /**
   * A concise summary of the issue.
   */
  issue_summary: string;

  /**
   * How the issue might affect the reader's experience.
   */
  reader_impact: string;

  /**
   * A more detailed explanation of the problem.
   */
  problem_detail: string;

  /**
   * A suggestion for how to address the issue.
   */
  recommendation: string;

  /**
   * Specific ideas for implementing the recommendation.
   * This can be either a string array or an array of ImplementationIdea objects.
   */
  implementation_ideas: (string | ImplementationIdea)[];

  /**
   * How addressing the issue aligns with the author's intentions for the story.
   * Renamed from author_intentions_alignment to be more general.
   */
  relation_to_author_intentions?: string; // Renamed for broader use

  /**
   * Legacy field for backward compatibility
   */
  author_intentions_alignment?: string;
}

/**
 * Represents the overall structure of the roadmap data, organized by chapter/part.
 */
export interface RoadmapChapterSummary {
  /**
   *  Key is a string like "roadmap-summary-by-chapter:part-i"
   */
  [key: string]: RoadmapSummaryByChapter;
}

/**
 * Represents the overall assessment of the manuscript.
 */
export interface OverallAssessment {
  /**
   * A general summary of the manuscript's strengths and weaknesses.
   */
  summary: string;

  /**
   * An overview of the analysis process and methodology.
   */
  overview: string;

  /**
   *  How well the manuscript aligns with the author's stated intentions.
   */
  author_intentions_alignment: string;
}

/**
 *  Represents the overall feedback data structure.
 */
export interface RoadmapSummaryOverall {
  /**
   * The overall assessment of the manuscript.
   */
  overall_assessment: OverallAssessment;
  /**
   *  The top areas identified for improvement.  Uses the same `Issue` interface as chapter feedback.
   */
  top_areas_for_improvement: Issue[];
}

// type FeedbackData =
//   | { type: "chapter"; data: RoadmapData }
//   | { type: "overall"; data: OverallFeedback };

// function processFeedback(feedback: FeedbackData) {
//   if (feedback.type === "chapter") {
//     // Now TypeScript knows feedback.data is RoadmapData
//     const chapterData = feedback.data;
//     for (const chapterKey in chapterData) {
//       const chapterSummary = chapterData[chapterKey];
//       console.log(
//         `Chapter: ${chapterSummary.friendly_id}, Function: ${chapterSummary.chapter_function}`
//       );
//       // ... process chapter data
//     }
//   } else if (feedback.type === "overall") {
//     // Now TypeScript knows feedback.data is OverallFeedback
//     const overallData = feedback.data;
//     console.log(
//       `Overall Assessment Summary: ${overallData.overall_assessment.summary}`
//     );
//     // ... process overall feedback
//   }
// }

// // Example Usage:

// const chapterFeedbackExample: FeedbackData = {
//     type: 'chapter',
//     data: {
//       "roadmap-summary-by-chapter:part-i": {
//           issues: [], //Populate with actual issues.
//           friendly_id: "part-1",
//           chapter_number: "1",
//           chapter_function: "",
//           analysis_overview: "",
//           chapter_strengths: ""
//       }
//     }
// };

// const overallFeedbackExample: FeedbackData = {
//     type: 'overall',
//     data: {
//         overall_assessment: {
//             summary: "The manuscript is good.",
//             overview: "Overview text.",
//             author_intentions_alignment: "Aligned."
//         },
//         top_areas_for_improvement: [] //Populate with actual Issues
//     }
// };

// processFeedback(chapterFeedbackExample);
// processFeedback(overallFeedbackExample);
